import React, { useState } from 'react';
import { Field, Form } from 'react-final-form';
import { useI18n } from '@locales/client';

import { FormField } from '@/shared/ui/form/form-field';
import { validateSchema } from '@/shared/libs/validate';
import { Box } from '@/shared/ui/box';
import { CheckboxField } from '@/shared/ui/form/checkbox-field';
import {
  BodyTextBoldSecondary,
  BodyTextRegularSecondary,
} from '@/shared/ui/typography';
import { formatCurrency } from '@/shared/libs/format-currency';
import { useUser } from '@/shared/providers/user-provider/use-user';
import { NumberInputField } from '@/shared/ui/form/number-input-field';

import { withdrawAddressSchema } from '@/features/wallet-managment/model/schemas';
import { useWithdrawForm } from '@/features/wallet-managment/model/hooks';
import { populateFieldsWithPreviousValues } from '@/features/wallet-managment/model/utils';
import { AdressesDetailCrypt } from '../address-detail';
import { MethodUnavailable } from './ui/method-unavailable';
import { WalletSkeleton } from '../wallet-skeleton';

import { MaxAmountButton, WithdrawButton, FormGroup } from './styles';

interface WithdrawFormProps {
  currency?: string; // Optional currency prop
}

const WithdrawForm: React.FC<WithdrawFormProps> = ({
  currency,
  setCurrentStep,
  setCurrentTransaction,
}) => {
  const {
    createPaymentTrigger,
    transformTransaction,
    saveAddress,
    limitCurrency,
    isLoadingLimited,
  } = useWithdrawForm(currency);

  const [form, setForm] = useState({
    amount: '',
    address: '',
    save: true,
  });

  const t = useI18n();
  const { user } = useUser();

  const schema = withdrawAddressSchema(
    {
      regex: currency?.walletRegex,
      minAmount: limitCurrency?.min,
      maxAmount: limitCurrency?.max,
    },
    t
  );
  const validate = validateSchema(schema);

  const submitWithdraw = async (values) => {
    const { address, amount, ...rest } = values;
    const params = {
      address,
      amount,
      currency: currency.code,
    };
    const paramsSave = {
      name: currency?.name,
      currency: currency.code,
      address: JSON.stringify(params),
      method: currency.code,
    };

    try {
      const transaction = await createPaymentTrigger(params);
      if (values?.save) {
        await saveAddress(paramsSave);
      }
      const transformedTransaction = transformTransaction(
        transaction,
        t('code')
      );
      setCurrentTransaction(transformedTransaction);
      setCurrentStep('history-detail');
    } catch (error) {
      // console.error('Error during withdrawal submission:', error);
    }
  };

  // Проверка доступности метода
  if (
    (+limitCurrency?.min === 0 && +limitCurrency?.max === 0) ||
    +limitCurrency?.min > +limitCurrency?.max
  ) {
    return (
      <MethodUnavailable limit={limitCurrency} symbol={user.currencySymbol} />
    );
  }

  return (
    <Form onSubmit={submitWithdraw} validate={validate} initialValues={form}>
      {(props) => {
        return (
          <FormGroup>
            <Box $display="flex" $flexDirection="column" $gap="24px">
              {isLoadingLimited && <WalletSkeleton t={t} />}

              <Box $display="flex" $flexDirection="column" $gap="12px">
                <Box
                  $display="flex"
                  $justifyContent="center"
                  $gap={4}
                  $alignItems="center"
                >
                  <BodyTextRegularSecondary color="#AFAFAF">
                    {t('wallet.withdraw.min_amount')}:
                  </BodyTextRegularSecondary>
                  <BodyTextRegularSecondary>
                    {formatCurrency(limitCurrency?.min, {
                      currency: user.currency,
                      currencySymbol: user.currencySymbol,
                    })}
                  </BodyTextRegularSecondary>
                </Box>
                <Field
                  name="amount"
                  placeholder={'1200'}
                  amount
                  label={
                    <BodyTextBoldSecondary>
                      {t('wallet.withdraw.amount')}
                    </BodyTextBoldSecondary>
                  }
                  type="text"
                  component={NumberInputField}
                />
              </Box>
              <Box>
                <MaxAmountButton>
                  <Box
                    $display="flex"
                    $gap={4}
                    $alignItems={'center'}
                    onClick={() =>
                      props.form.change('amount', limitCurrency?.max)
                    }
                  >
                    <BodyTextRegularSecondary>
                      {formatCurrency(limitCurrency?.max, {
                        currency: user.currency,
                        currencySymbol: user.currencySymbol,
                      })}
                    </BodyTextRegularSecondary>
                    <BodyTextBoldSecondary>
                      {t('wallet.withdraw.max')}
                    </BodyTextBoldSecondary>
                  </Box>
                </MaxAmountButton>
              </Box>
              <AdressesDetailCrypt
                inputValue={props?.values}
                keyAddres={currency?.code}
                onEdit={(address) =>
                  populateFieldsWithPreviousValues(props, address)
                }
              />
              <Box $display="flex" $flexDirection="column" $gap="16px">
                <BodyTextBoldSecondary>
                  {t('wallet.deposit.enter_data')}
                </BodyTextBoldSecondary>
                <Field
                  name="address"
                  component={FormField}
                  label={
                    <BodyTextBoldSecondary style={{ textAlign: 'left' }}>
                      {t('wallet.withdraw.target_address')}
                    </BodyTextBoldSecondary>
                  }
                  type="text"
                  placeholder={t('wallet.withdraw.address', {
                    token: currency.code,
                  })}
                />
                <Box $textAlign="left">
                  <Field
                    name="save"
                    component={CheckboxField}
                    type="checkbox"
                    label={
                      <BodyTextRegularSecondary>
                        {t('wallet.fiat.input_fields.sections.save')}
                      </BodyTextRegularSecondary>
                    }
                  />
                </Box>
              </Box>
              <Box
                $position="absolute"
                $bottom="18px"
                $borderRadius="8px"
                $opacity="1"
                $width="calc(100% - 36px)"
                $zIndex="100"
                $backgroundColor="#3D3C41"
              >
                <WithdrawButton
                  onClick={() => props.form.submit()}
                  disabled={props.submitting || props.form.getState().invalid}
                  variant={
                    props.submitting || props.form.getState().invalid
                      ? 'gray'
                      : 'orangeGradient'
                  }
                >
                  <BodyTextBoldSecondary>
                    {t('wallet.withdraw.withdraw.submit', {
                      amount: formatCurrency(props.values.amount, {
                        currency: user.currency,
                        currencySymbol: user.currencySymbol,
                      }),
                    })}
                  </BodyTextBoldSecondary>
                </WithdrawButton>
              </Box>
            </Box>
          </FormGroup>
        );
      }}
    </Form>
  );
};

export { WithdrawForm };
